import { createFileRoute } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { AppLink } from "@/components/nav/app-link";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { MetricCard } from "@/components/display/metric-card";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/feedback/states";
import { LiveActivity } from "@/components/dashboard/live-activity";
import { RunsChart } from "@/components/dashboard/runs-chart";
import { dashboardQuery, useDashboardStats } from "@/api/monitoring";
import { recentExecutionsQuery, type Execution, useRecentExecutions } from "@/api/executions";
import { usePipelineNames } from "@/api/pipelines";
import { useLiveInvalidate } from "@/lib/live";
import { fmtDuration, fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_app/")({
  loader: ({ context: { queryClient } }) => {
    void queryClient.prefetchQuery(dashboardQuery);
    void queryClient.prefetchQuery(recentExecutionsQuery({ size: 40 }));
  },
  component: DashboardPage,
});

function DashboardPage() {
  const stats = useDashboardStats();
  const recent = useRecentExecutions({ size: 40 });
  const names = usePipelineNames();

  useLiveInvalidate([
    ["monitoring", "dashboard"],
    ["executions", "recent"],
  ]);

  const s = stats.data;
  const successRate =
    s && s.executionsToday > 0 ? Math.round((s.successfulToday / s.executionsToday) * 100) : null;
  const runs = recent.data?.content;

  const columns: Column<Execution>[] = [
    {
      id: "pipeline",
      header: "Pipeline",
      cell: (r) =>
        r.pipelineId ? (
          <AppLink to="/pipelines/$pipelineId" params={{ pipelineId: r.pipelineId }}>
            {names[r.pipelineId] ?? r.pipelineId}
          </AppLink>
        ) : (
          "—"
        ),
    },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    {
      id: "records",
      header: "Records",
      align: "right",
      cell: (r) => fmtNumber(r.recordsWritten ?? r.recordsRead),
    },
    { id: "duration", header: "Duration", align: "right", cell: (r) => fmtDuration(r.durationMs) },
    { id: "when", header: "Started", cell: (r) => <RelativeTime value={r.startedAt} /> },
  ];

  return (
    <PageContainer>
      <PageHeader title="Dashboard" description="Platform health at a glance." />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        <MetricCard
          label="Runs today"
          value={fmtNumber(s?.executionsToday)}
          animateValue={s?.executionsToday ?? null}
          hint={s ? `${fmtNumber(s.running)} running now` : undefined}
          loading={stats.isLoading}
        />
        <MetricCard
          label="Success rate"
          value={successRate == null ? "—" : `${successRate}%`}
          tone={successRate != null && successRate < 90 ? "warning" : "success"}
          loading={stats.isLoading}
        />
        <MetricCard
          label="Failed today"
          value={fmtNumber(s?.failedToday)}
          animateValue={s?.failedToday ?? null}
          tone={s && s.failedToday > 0 ? "error" : "default"}
          loading={stats.isLoading}
        />
        <MetricCard
          label="Active pipelines"
          value={fmtNumber(s?.activePipelines)}
          hint={s ? `of ${fmtNumber(s.totalPipelines)} total` : undefined}
          loading={stats.isLoading}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        }}
      >
        <LiveActivity running={s?.running ?? 0} />
        <RunsChart runs={runs} />
      </Box>

      <PageHeader title="Recent runs" />
      <Card>
        <DataTable
          columns={columns}
          data={runs}
          isLoading={recent.isLoading}
          error={recent.error}
          onRetry={() => recent.refetch()}
          rowKey={(r) => r.id}
          pageSize={10}
          empty={
            <EmptyState
              title="No runs yet"
              description="Trigger a pipeline and its runs will show up here."
            />
          }
        />
      </Card>
    </PageContainer>
  );
}
